<template>
  <div
    ref="defaultCollection"
    class="koc-view koc-content-search-view"
    data-observe-resize
    data-observe-theme
  >
    <Modal
      :item="state.modalItem"
      :show="state.showModal"
      @toggle-modal="toggleModal"
    />
    <h1
      v-if="!state.hasResults"
      class="koc-title-2 koc-row-center"
    >
      No Results Found...
    </h1>
    <section
      v-for="(section, index) in state.results"
      v-else
      :key="section.type"
      :class="'koc-section-' + section.label.toLowerCase()"
      class="koc-section"
    >
      <header
        v-if="0 === index"
        class="koc-section-header"
        data-children="Page Title"
      >
        <div class="koc-section-header-main">
          <div class="koc-full">
            <div class="koc-title-1">
              Search Results
            </div>
          </div>
        </div>
      </header>
      <header
        class="koc-section-header koc-filter-container"
        data-children="Search — Sorting — Filters"
      >
        <div
          class="koc-section-header-main"
          data-children="Search — Sorting"
        >
          <div class="koc-two-thirds">
            <form-input-component
              :value="state.term"
              label="Search"
              name="term"
              placeholder="Search"
              type="search"
              @update="debounce"
            />
          </div>
          <div
            class="koc-one-third koc-sorting-options"
            style="clear: both;"
          >
            <template v-if="state.sortByFilter">
              <div
                class="koc-search-sorting-wrapper koc-filter koc-full"
              >
                <div
                  :key="state.sortByFilter.label"
                  class="koc-filter koc-full"
                >
                  <filter-select-component
                    :label="state.sortByFilter.label"
                    :options="state.sortByFilter.data"
                    :placeholder="sortByPlaceholderText(state.sortByFilter)"
                    permanent-label="true"
                    @selected="apply"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
        <div
          class="koc-section-header-main"
          data-children="Filters"
        >
          <div class="koc-filters-wrapper">
            <div class="koc-filters">
              <template v-if="state.hasResults && 1 === state.results.length">
                <div
                  v-if="state.availableFilters.length"
                  class="koc-section-filters koc-body-1 koc-one-third"
                >
                  <span>Filters:</span>
                  <div
                    v-for="filter in state.availableFilters"
                    :key="filter.label"
                    class="koc-filter koc-full"
                  >
                    <filter-select-component
                      :label="filter.label"
                      :options="filter.data"
                      :placeholder="filter.label"
                      @selected="apply"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </header>

      <section
        v-if="state.activeFilters.length"
        class="koc-section-header koc-active-filters"
      >
        <div class="koc-section-header-main">
          <div class="koc-body-1 koc-flex koc-active-filters">
            <div
              v-for="filter in state.activeFilters"
              :key="filter.label"
              class="koc-active-filter"
            >
              <div class="koc-filter">
                {{ filter.label }}:&nbsp;
              </div>
              <span
                v-for="taxonomy in filter.active"
                :key="taxonomy.label"
                class="koc-chip"
                @click="apply(taxonomy)"
              >
                {{ taxonomy.label }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <header class="koc-section-header koc-tabs">
        <div class="koc-section-header-main">
          <div class="koc-flex-start">
            <div
              v-for="type in types"
              :key="type.key"
              :class="{ active: type.label === section.label }"
              class="koc-title-2 koc-filter-link"
              @click="filterByType(type.key.toLowerCase())"
            >
              {{ type.label }}
            </div>
          </div>
        </div>
      </header>

      <main
        v-if="state.loading"
        class="koc-section-items"
      >
        <div
          v-for="n in state.skeletonCount"
          :key="n"
          class="koc-section-item-skeleton"
        >
          <SkeletonLoader
            v-if="state.activeType === 'article'"
            :image="true"
            :content="true"
            :content-body="1"
            :footer="true"
          />
          <SkeletonLoader
            v-if="state.activeType === 'webcast'"
            :image="true"
            :content="true"
            :footer="true"
            :footer-icons="1"
          />
          <SkeletonLoader
            v-if="state.activeType === 'discount'"
            :image="true"
            :content="true"
            :content-body="1"
            :footer="true"
          />
          <SkeletonLoader
            v-if="state.activeType === 'course'"
            :image="true"
            :content="true"
            :content-body="1"
            :content-body-flex="true"
            :footer="true"
            :footer-icons="3"
          />
          <SkeletonLoader
            v-if="state.activeType === 'tutorial'"
            :image="true"
            :content="true"
            :content-body="1"
            :footer="true"
          />
        </div>
      </main>

      <main
        v-else
        class="koc-section-items"
      >
        <section
          v-if="!section.results.length"
          class="text-center koc-title-1"
        >
          No Results Found
        </section>
        <div
          v-for="item in section.results"
          :key="item.id"
          class="koc-section-item koc-horizontal"
        >
          <Discount
            v-if="item.type === 'discount'"
            :item="item"
            :user="state.user"
          />
          <Tutorial
            v-if="item.type === 'tutorial'"
            :item="item"
            :user="state.user"
          />
          <Webcast
            v-if="item.type === 'webcast'"
            :item="item"
            :user="state.user"
            @toggle-modal="toggleModal"
          />
          <Article
            v-if="item.type === 'article'"
            :item="item"
            :user="state.user"
          />
          <Course
            v-if="item.type === 'course'"
            :item="item"
            :user="state.user"
            @toggle-modal="toggleModal"
          />
          <Magazine
            v-if="item.type === 'magazine'"
            :item="item"
            :user="state.user"
            @toggle-modal="toggleModal"
          />
        </div>
      </main>
    </section>
    <footer
      v-if="!state.loading && 1 === state.results.length"
      class="koc-section-footer"
    >
      <pagination-component
        :pagination="state.pagination"
        @change="paginate"
      />
    </footer>
  </div>
</template>

<script src="./search.js"></script>
<style lang="scss" scoped src="./search.scss"></style>
<style lang="scss" scoped>
.koc-filter-container {
  display: flex;
  flex-direction: column;
}

.koc-filter-link{
  margin: 0;
  padding: 16px;
}
.koc-light {
  background: #ffffff;
  color: #4c4c4c;
}

.koc-light-alternate {
  background: #e9e8e8;
  color: #4c4c4c;
}

.koc-dark {
  background: #4c4c4c;
  color: #ffffff;
}

.koc-dark-alternate {
  background: #292929;
  color: #ffffff;
}

.koc-primary {
  background: #ffa400;
  color: #292929;
}

.koc-secondary {
  background: #13b1b4;
  color: #292929;
}

.koc-tertiary {
  background: #994ea1;
  color: #e9e8e8;
}

.koc-success {
  background: #93AC07;
  color: #292929;
}

.koc-warning {
  background: #FFD26D;
  color: #292929;
}

.koc-danger {
  background: #E31E26;
  color: #ffffff;
}

.koc-flex-start {
  display: flex;
  justify-content: flex-start;
}

.koc-section {
  background-color: var(--koc-body-background);
}

.koc-section-header {
  background: var(--koc-card-background);
  color: var(koc-default-text);
  display: flex;
  padding-top: 16px;

  .koc-section-header-main {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: 0 auto;
    max-width: 1968px;
    width: 100%;

    .koc-title-2 {
      padding-bottom: 0;
      padding-top: 0;
      position: relative;

      &.koc-link {
        color: var(--koc-secondary-color);
        cursor: pointer;
      }

      &.active {
        color: var(--koc-color-primary);

        &:after {
          background-color: var(--koc-color-primary);
          bottom: -16px;
          content: '';
          height: 4px;
          left: 4px;
          position: absolute;
          width: calc(100% - 8px);
        }
      }
    }

    .koc-section-filters {
      display: flex;
      flex-grow: 1;
      justify-content: flex-end;
      justify-self: flex-end;
      padding: 0 0 0 8px;
      width: 100%;

      span {
        line-height: 2;
        padding-right: 8px;
      }

      .koc-filter {
        float: left;
        height: 62px;
        min-width: 230px;
        position: relative;
      }

      .koc-button {
        color: #fff;
        line-height: 1.2;
        margin: 0;
      }
    }

    .koc-view-all {
      margin-left: 12px;

      .koc-button {
        margin: 0;
      }
    }
  }
}

.koc-section {
  background: var(--koc-body-background);

  .koc-section-item-header {
    background: var(--koc-card-background);
  }

  .koc-section-item-main {
    .koc-footer {
      padding: 8px 0;
      width: 100%;
    }
  }

  .koc-image {
    padding-top: 56.25%;
    position: relative;
    width: 100%;

    .koc-thumbnail {
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
}

.koc-section-items {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 auto;
  max-width: 2000px;
  padding: 4px;

  .koc-section-item {
    display: flex;
    flex-basis: 300px;
    flex-basis: calc(25% - 8px);
    flex-direction: column;
    flex-grow: 1;
    margin: 0px 4px;
    max-width: 478px;
    min-width: 304px;

    &-skeleton{
      display: flex;
      flex-basis: 300px;
      flex-basis: calc(25% - 8px);
      flex-direction: column;
      flex-grow: 1;
      margin: 4px;
      max-width: 478px;
      min-width: 304px;
    }

    .koc-section-item-main {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      position: relative;

      .koc-details {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: flex-start;
        width: 100%;

        .koc-content {
          flex-grow: 1;
          width: 100%;

          .koc-title-2 {
            padding-bottom: 8px;

            a {
              color: var(--koc-default-text);
            }
          }

          .koc-instructor {
            a {
              color: var(--koc-default-text);
            }

            span + span {
              &:before {
                content: '|';
                padding: 0 4px;
              }
            }
          }

          p:not(:last-of-type) {
            margin-bottom: 8px;
          }
        }

        p {
          margin: 0 0 16px 0;
          padding: 0 16px;
        }
      }

      &.koc-vertical {
        display: flex;
        flex-direction: column;

        .koc-image {
          width: 100%;

          img {
            float: left;
            margin: 0;
            padding: 0;
            width: 100%;
          }
        }

        .koc-details {
          width: 100%;
        }
      }

      &[class*=koc-horizontal] {
        display: flex;
        flex-direction: row;

        .koc-image {
          width: 33.33%;

          img {
            float: left;
            height: 100%;
            margin: 0;
            object-fit: cover;
            padding: 0;
            width: 100%;
          }
        }

        .koc-details {
          width: 66.64%;
        }
      }

      &.koc-horizontal-right {
        flex-direction: row-reverse;
      }

      &.koc-overlay {
        // display: flex;
        // flex-direction: row;
        &.koc-light {
          .koc-details {
            background: rgba(232, 232, 232, 0.34);
            color: #0d0d0d;
          }
        }

        &.koc-dark {
          .koc-details {
            background: rgba(13, 13, 13, 0.34);
            color: #e9e8e8;
          }
        }

        .koc-image {
          width: 100%;

          img {
            float: left;
            margin: 0;
            padding: 0;
            width: 100%;
          }
        }

        .koc-details {
          align-items: center;
          bottom: 0;
          display: flex;
          height: 100%;
          justify-content: center;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: 100%;
        }
      }
    }

    &.koc-item-1-1 {
      flex-basis: calc(100% - 16px);
    }

    &.koc-item-3-4 {
      flex-basis: calc(75% - 16px);
    }

    &.koc-item-2-3 {
      flex-basis: calc(66.67% - 16px);
    }

    &.koc-item-1-2 {
      flex-basis: calc(50% - 16px);
    }

    &.koc-item-1-3 {
      flex-basis: calc(33.33% - 16px);
    }

    &.koc-item-1-4 {
      flex-basis: calc(25% - 16px);
    }

    &.koc-item-1-5 {
      flex-basis: calc(20% - 16px);
    }

    &.koc-item-1-6 {
      flex-basis: calc(16.6% - 16px);
    }
  }
}

.koc-active-filters {
  display: flex;
  justify-content: flex-start;
  padding: 0 12px;

  * {
    float: left;
  }
}

.koc-chip {
  background-color: var(--koc-secondary-color);
  border-radius: 20px;
  color: var(--koc-color-secondary-contrast);
  cursor: pointer;
  line-height: 20px;
  margin: 0 8px;
  padding: 4px 36px 4px 12px;
  position: relative;

  & ~ .koc-chip {
    margin-left: 0;
  }

  &:after {
    content: 'X';
    position: absolute;
    right: 10px;
    text-align: center;
    width: 16px;
  }
}

.koc-palette {
  bottom: 0;
  //display: none;
  //left: 0;
  height: 100%;
  mask-image: -webkit-gradient(linear, left bottom, left top,
    from(rgba(0, 0, 0, 0)), to(rgba(0, 0, 0, 1)));
  position: absolute;
  right: -100%;
  top: 0;
  width: 175%;
}

header {
  .koc-palette {
    right: 0;
    width: 87.5%;
  }
}

.koc-search-sorting-wrapper {
}

.koc-filters-wrapper{
  width: 100%;
  margin-top: 4em;
  @media(min-width: 1201px) {
    margin-top: 0;
  }
}

/* Content styles */
.koc-content {
  //background: var(--koc-background-alternate);
  //  border-radius: 3px;
  //  color: #fff;
  //  margin: 0 auto;
  //  position: relative;
  //padding: 24px;
}

//.koc-content h3 {
//  background: rgba(0, 0, 0, 0.1);
//  border-radius: 3px 3px 0 0;
//  font-size: 2.4em;
//  font-weight: 300;
//  margin: 0;
//  opacity: 0.8;
//  padding: 0.4em;
//  text-align: center;
//}

//.koc-content > div {
//  font-size: 1.15em;
//  font-weight: 300;
//  margin: 0;
//  padding: 15px 40px 30px;
//}
//
//.koc-content > div p {
//  margin: 0;
//  padding: 10px 0;
//}
//
//.koc-content > div ul {
//  margin: 0;
//  padding: 0 0 30px 20px;
//}
//
//.koc-content > div ul li {
//  padding: 5px 0;
//}
//
//.koc-content button {
//  display: block;
//  font-size: 0.8em;
//  margin: 0 auto;
//}
</style>
